import React, { FocusEventHandler, useCallback, useRef } from 'react';

const getNthVerticalSibling = (element: HTMLElement | null, offset: number): HTMLElement | null => {
  const n = Math.abs(offset);
  let result: HTMLElement | null = null;
  if (!element) return result;

  if (offset === 0) {
    result = element;
  } else if (offset < 0) {
    let nthPreviousSibling = element.previousElementSibling;
    for (
      let i = 0;
      nthPreviousSibling?.previousElementSibling &&
      (i < n - 1 || nthPreviousSibling?.getBoundingClientRect().top === element.getBoundingClientRect().top);
      i++
    ) {
      nthPreviousSibling = nthPreviousSibling.previousElementSibling;
    }
    result = nthPreviousSibling ? (nthPreviousSibling as HTMLElement) : null;
  } else {
    let nthNextSibling = element.nextElementSibling;
    for (
      let i = 0;
      nthNextSibling?.nextElementSibling &&
      (i < n - 1 || nthNextSibling?.getBoundingClientRect().top === element.getBoundingClientRect().top);
      i++
    ) {
      nthNextSibling = nthNextSibling.nextElementSibling;
    }
    result = nthNextSibling ? (nthNextSibling as HTMLElement) : null;
  }

  return result;
};

const scrollIntoViewIfNeeded = (
  element: HTMLElement,
  comparisonElement?: HTMLElement | null,
  numberOfPreviousElementsOnScroll?: number
) => {
  comparisonElement = comparisonElement ?? element;
  const offsetParent = comparisonElement.offsetParent;
  if (offsetParent instanceof HTMLElement) {
    const firstSibling = Array.from(element.parentElement?.children ?? []).find(
      (e) => e instanceof HTMLElement
    ) as HTMLElement | null;
    const baseOffsetY: number = firstSibling?.offsetTop ?? 0;
    const parentRect = offsetParent.getBoundingClientRect();
    const visibleRegionTop = window.scrollY + parentRect.top;
    const visibleRegionBottom = Math.min(window.scrollY + parentRect.bottom, window.innerHeight);
    const visibleRegionHeight = visibleRegionBottom - visibleRegionTop;

    const comparisonRect = comparisonElement.getBoundingClientRect();
    if (comparisonRect.top < visibleRegionTop) {
      // scrolling direction is up
      const nextSibling = getNthVerticalSibling(element, numberOfPreviousElementsOnScroll ?? 0) ?? element;
      const scrollY =
        window.scrollY + nextSibling.getBoundingClientRect().bottom - visibleRegionTop - visibleRegionHeight;
      window.scrollTo({ top: scrollY, behavior: 'auto' });
    } else if (comparisonRect.bottom > visibleRegionBottom) {
      // scrolling direction is down
      const nextSibling = getNthVerticalSibling(element, -(numberOfPreviousElementsOnScroll ?? 0)) ?? element;
      const scrollY = window.scrollY + nextSibling.getBoundingClientRect().top - visibleRegionTop - baseOffsetY;
      window.scrollTo({ top: scrollY, behavior: 'auto' });
    }
  }
};

export const useFocusScroll = <T extends HTMLElement>(
  siblingElementBuffer: number,
  useParentElement: boolean // used for virtualized grid rows that have contain a wrapping div
): [(element: T | null) => void, FocusEventHandler] => {
  siblingElementBuffer = Math.max(siblingElementBuffer, 0);
  const isMouseOverElement = useRef<boolean>(false);
  const handleMouseover = useCallback(() => {
    isMouseOverElement.current = true;
  }, []);
  const handleMouseout = useCallback(() => {
    isMouseOverElement.current = false;
  }, []);
  const callbackRef = (element: T | null) => {
    if (element) {
      element.removeEventListener('mouseover', handleMouseover);
      element.removeEventListener('mouseout', handleMouseout);

      element.addEventListener('mouseover', handleMouseover);
      element.addEventListener('mouseout', handleMouseout);
    }
  };

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node) || isMouseOverElement.current) {
        return;
      }
      const element = event.currentTarget;
      if (element) {
        // const firstSibling = Array.from(element.parentElement?.children ?? []).find(
        //   (e) => 'offsetTop' in e
        // ) as HTMLElement;
        const offsetParent = element.offsetParent;
        if (offsetParent /*&& firstSibling*/) {
          const relatedTarget = event.relatedTarget as HTMLElement;
          if (relatedTarget) {
            const elementRect = element.getBoundingClientRect();
            const relatedTargetRect = relatedTarget.getBoundingClientRect();
            if (relatedTargetRect.top < elementRect.top || relatedTargetRect.bottom > elementRect.bottom) {
              // Table rows are wrapper in a box element for virtualization - look at the parent element
              const rootElement = useParentElement ? element.parentElement : element;
              if (relatedTargetRect.top < elementRect.top /*&& element !== firstSibling*/) {
                // scrolling down
                scrollIntoViewIfNeeded(element, getNthVerticalSibling(rootElement, 1), siblingElementBuffer);
              } else if (relatedTargetRect.top > elementRect.top) {
                // scrolling up
                scrollIntoViewIfNeeded(element, getNthVerticalSibling(rootElement, -1), siblingElementBuffer);
              }
            }
          }
        }
      }
    },
    [siblingElementBuffer]
  );

  return [callbackRef, handleFocus];
};
